import { X } from 'lucide-react';

const LEGAL = {
  company: process.env.REACT_APP_LEGAL_COMPANY_NAME || 'AWN GmbH',
  address: process.env.REACT_APP_LEGAL_ADDRESS || 'Konrad-Zuse-Str. 1, 84579 Unterneukirchen, Germany',
  representative: process.env.REACT_APP_LEGAL_REPRESENTATIVE || 'Richard Langlechner',
  email: process.env.REACT_APP_LEGAL_EMAIL || 'info@strom-aus-abgas.de',
  phone: process.env.REACT_APP_LEGAL_PHONE || '+49 (0)8634 - 624 336',
  register: process.env.REACT_APP_LEGAL_REGISTER || 'Amtsgericht Traunstein, HRB 22358',
  vatId: process.env.REACT_APP_LEGAL_VAT_ID || 'DE288084414',
};

const Missing = ({ label }) => <span className="legal-missing">{label} must be configured before production.</span>;

function PrivacyPolicy() {
  return (
    <>
      <h3>Privacy Policy</h3>
      <p>Version: 24 July 2026</p>
      <h4>Controller and contact</h4>
      <p>
        {LEGAL.company || <Missing label="Legal operator / controller name" />}<br />
        {LEGAL.address || <Missing label="Registered business address" />}<br />
        Privacy contact: {LEGAL.email || <Missing label="Privacy email address" />}<br />
        Phone: {LEGAL.phone || <Missing label="Contact telephone number" />}
      </p>
      <h4>Data we process</h4>
      <p>
        We process account identity and authentication data, profile and application preferences, company and product
        information, uploaded brand files and documents, campaign prompts and generated outputs, feedback, support/contact
        messages, privacy choices, and technical security/hosting logs. Google OAuth is used only when you choose it.
      </p>
      <h4>Purposes and legal bases</h4>
      <p>
        Account and generation data is processed to provide the service and take requested pre-contractual steps
        (GDPR Article 6(1)(b)). Security, abuse prevention, service reliability, and necessary business records are
        processed for legitimate interests or legal obligations (Articles 6(1)(f) and 6(1)(c)). Optional terminal
        storage and third-party editor fonts are used only with your consent (Article 6(1)(a)); consent can be withdrawn
        without affecting prior lawful processing.
      </p>
      <h4>Recipients and international transfers</h4>
      <p>
        The service uses Railway for application hosting, Supabase for authentication/database/storage, and Google for
        optional OAuth and configured Gemini generation services. Submitted generation content may therefore be sent to
        the configured model provider. Providers may process data outside the EEA under their applicable transfer
        safeguards. Do not upload confidential or special-category data unless the service has been contractually
        approved for that use.
      </p>
      <h4>Retention</h4>
      <p>
        Account content is retained while the account is active and removed when account deletion completes, except where
        a legal retention duty applies. Contact requests are retained only as long as needed to answer and document the
        request. Provider security logs follow the configured provider retention periods.
      </p>
      <h4>Your rights</h4>
      <p>
        Depending on the circumstances, you may request access, correction, deletion, restriction, portability, or object
        to processing, and you may lodge a complaint with a competent supervisory authority. Signed-in users can export
        their account data or permanently delete their account from Profile &gt; Privacy &amp; Data.
      </p>
    </>
  );
}

function CookiePolicy() {
  return (
    <>
      <h3>Cookie and Local Storage Policy</h3>
      <p>Version: 24 July 2026</p>
      <p>
        AWN AdPulse does not currently install analytics, advertising, or retargeting trackers. The browser may store:
      </p>
      <ul>
        <li><strong>Supabase authentication storage (necessary):</strong> keeps a signed-in session secure and refreshes it.</li>
        <li><strong>adpulseConsentV1 (necessary):</strong> remembers consent categories, version, action, and time until deleted or the policy version changes.</li>
        <li><strong>adpulsePreferences (functional):</strong> remembers visitor language, theme, and notification choices only after functional consent.</li>
        <li><strong>adpulse.tutorial-progress (functional):</strong> provides a local fallback for visitor tutorial progress. Signed-in progress is synchronized to the account database.</li>
      </ul>
      <p>
        Google-hosted interface and graphics-editor fonts are fetched only after functional consent. Google OAuth
        contacts Google only after you select "Continue with Google." You can reopen
        Cookie Preferences from every page footer and withdraw optional choices at any time.
      </p>
    </>
  );
}

function Terms() {
  return (
    <>
      <h3>Terms and Conditions</h3>
      <p>Version: 24 July 2026</p>
      <p>
        AWN AdPulse provides AI-assisted campaign planning, text, image, and video generation tools. You must provide
        accurate account details, keep credentials secure, and use the service lawfully. You remain responsible for
        uploaded material, instructions, generated outputs, final review, required disclosures, intellectual-property
        clearance, and publication decisions.
      </p>
      <p>
        Do not submit unlawful, infringing, malicious, deceptive, or sensitive personal data without a valid legal basis.
        AI outputs can be incomplete or incorrect and are not professional legal, medical, or financial advice. Service
        availability and model behavior may change for security, provider, maintenance, or capacity reasons.
      </p>
      <p>
        The current application exposes a free plan and does not complete paid purchases. Any future paid subscription,
        renewal, cancellation, and refund terms must be presented before purchase. You may stop using the service or
        delete your account in account settings. Mandatory consumer and statutory rights remain unaffected.
      </p>
    </>
  );
}

function LegalNotice() {
  return (
    <>
      <h3>Legal Notice (Impressum)</h3>
      <p>
        <strong>{LEGAL.company || <Missing label="Legal operator name and legal form" />}</strong><br />
        {LEGAL.address || <Missing label="Registered business address" />}<br />
        Represented by: {LEGAL.representative || <Missing label="Managing director / legal representative" />}<br />
        Email: {LEGAL.email || <Missing label="Contact email address" />}<br />
        Phone: {LEGAL.phone || <Missing label="Contact telephone number" />}<br />
        Register: {LEGAL.register || <Missing label="Commercial register and registration number" />}<br />
        VAT ID: {LEGAL.vatId || <Missing label="VAT identification number, if applicable" />}
      </p>
    </>
  );
}

function LegalCenter({ open, onClose, onSelect }) {
  if (!open) return null;
  const title = open === 'cookies' ? 'Cookie Policy' : open === 'terms' ? 'Terms' : open === 'legal' ? 'Legal Notice' : 'Privacy Policy';
  return (
    <div className="settings-overlay legal-overlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="legal-modal panel glow-card" role="dialog" aria-modal="true" aria-label={title}>
        <button type="button" className="settings-close" onClick={onClose} aria-label="Close legal information"><X size={18} /></button>
        <nav className="legal-tabs" aria-label="Legal documents">
          {[
            ['privacy', 'Privacy'],
            ['cookies', 'Cookies'],
            ['terms', 'Terms'],
            ['legal', 'Legal notice'],
          ].map(([key, label]) => (
            <button type="button" key={key} className={open === key ? 'active' : ''} onClick={() => onSelect(key)}>{label}</button>
          ))}
        </nav>
        <article className="legal-copy">
          {open === 'privacy' && <PrivacyPolicy />}
          {open === 'cookies' && <CookiePolicy />}
          {open === 'terms' && <Terms />}
          {open === 'legal' && <LegalNotice />}
        </article>
      </section>
    </div>
  );
}

export default LegalCenter;
